import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Intervention,
  InterventionScopeNameEnum,
} from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import Trans from '@lib/components/Trans/Trans';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import useSearchParam from '@lib/hooks/useSearchParam';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import useInterventionActions from 'routes/Interventions/utils/useActions';
import useInterventionModalManagement from 'routes/Interventions/utils/useModalManagement';
import getDepartmentInterventionsTableColumns from '../utils/getDepartmentInterventionsTableColumns';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';

type Props = {
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentViewInterventions({
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { id = '' } = useParams();
  const [searchQuery] = useSearchParam();
  const useBulkSelectResult = useTableItemsBulkSelect<Intervention>();
  const useModalManagementResult = useInterventionModalManagement({
    useBulkSelectResult,
  });

  const { useDepartmentQueryHookResult } = useDepartmentQueriesResult;
  const { data } = useDepartmentQueryHookResult;
  const { name } = data || {};

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const {
    closeLoading,
    interventionsQueryHook,
    onCloseModal,
    onConfirmedInterventionClose,
    onStatusChangeHandler,
  } = useInterventionActions({
    departmentId: id,
    interventionScope: InterventionScopeNameEnum.All,
    pathname,
    search,
    searchQuery,
    useBulkSelectResult,
    useModalManagementResult,
  });

  const onEditDepartment = () =>
    navigate(
      APP_URLS.dashboard.departments.edit.getDynamicPath({
        pathParts: { id },
      }),
    );

  const tableColumns = getDepartmentInterventionsTableColumns({
    onStatusChangeHandler,
    t,
  });

  const { collection, loading, firstLoading, error, totalCount } =
    interventionsQueryHook;

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-department-linked-interventions-yet')}
        text={t('no-department-linked-interventions-text')}
        actions={
          isViewOnly
            ? []
            : [
                <Button
                  key="edit"
                  buttonText={t('edit-department')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={onEditDepartment}
                  disabled={loading}
                />,
              ]
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search placeholder={t('asset-interventions-search-placeholder')} />
      )}
      <Table
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        id="departmentInterventions"
        loading={loading}
        totalEntries={totalCount}
      />
    </>
  ) : null;

  return (
    <>
      <WhiteBox>
        <DashboardTitleHeader
          title={name}
          actions={
            isViewOnly ? (
              <ViewOnlyChip key="view" />
            ) : (
              <Button
                key="edit"
                buttonText={t('edit')}
                buttonType={ButtonTypes.primaryOutlined}
                leftIcon="edit"
                onClick={onEditDepartment}
              />
            )
          }
        />
        {firstLoading && <CircleLoader />}
        {errorState}
        {emptyState}
        {content}
      </WhiteBox>
      <ConfirmationModal
        confirmButtonLabel={t('yes-close')}
        description={<Trans i18nKey="intervention-close-text" />}
        icon={IconName.Warning}
        isOpen={useModalManagementResult.isInterventionCloseModalOpened}
        loading={closeLoading}
        onClose={onCloseModal}
        onConfirm={onConfirmedInterventionClose}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}
